import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Search, TrendingUp, TrendingDown, Loader2, Download } from "lucide-react";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtK = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)} K`;
  return `₹${n.toFixed(0)}`;
};

const marginTone = (pct) => {
  if (pct >= 25) return { color: "#15803d", bg: "#dcfce7", label: "Healthy" };
  if (pct >= 10) return { color: "#c2410c", bg: "#ffedd5", label: "Tight" };
  if (pct >= 0)  return { color: "#a16207", bg: "#fef3c7", label: "Low" };
  return { color: "#b91c1c", bg: "#fee2e2", label: "Loss" };
};

export default function CostingMargin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("margin_amount"); // margin_amount | margin_percent | quote_value | total_cost
  const [sortDir, setSortDir] = useState("desc");
  const [stageFilter, setStageFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/costing/margin");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const stages = useMemo(() => {
    const s = new Set(rows.map((r) => r.stage).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows;
    if (needle) {
      out = out.filter((r) =>
        [r.project_id, r.name, r.customer_name, r.sales_person_name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    if (stageFilter !== "all") out = out.filter((r) => r.stage === stageFilter);
    out = [...out].sort((a, b) => {
      const va = Number(a[sortBy] || 0), vb = Number(b[sortBy] || 0);
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return out;
  }, [rows, q, stageFilter, sortBy, sortDir]);

  const totals = useMemo(() => {
    if (!filtered.length) return { quote: 0, spend: 0, margin: 0, pct: 0 };
    const qv = filtered.reduce((s, r) => s + Number(r.quote_value || 0), 0);
    const sp = filtered.reduce((s, r) => s + Number(r.total_cost || 0), 0);
    const mg = qv - sp;
    return { quote: qv, spend: sp, margin: mg, pct: qv > 0 ? (mg / qv) * 100 : 0 };
  }, [filtered]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("desc"); }
  };
  const arrow = (key) => sortBy === key ? (sortDir === "asc" ? "↑" : "↓") : "";

  return (
    <DesktopLayout>
      <div className="max-w-[1600px] mx-auto p-6 space-y-4" data-testid="costing-margin-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">Costing</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">Margin Dashboard</h1>
            <div className="text-xs text-slate-500 mt-1">
              Live rollup — sheets · POs · labour · transport · installation vs quote value.
              Visible to Super Admin & Accounts only.
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="margin-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Refresh
          </button>
          <a href={`${api.defaults.baseURL}/costing/margin.xlsx`}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            data-testid="margin-export-xlsx"
            title="Download Margin Dashboard as Excel (for Tally / CA)">
            <Download className="w-3.5 h-3.5" /> Export Excel
          </a>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="margin-totals">
          <TotalTile label="Projects" value={filtered.length} sub={`of ${rows.length}`} />
          <TotalTile label="Quote value" value={fmtK(totals.quote)} sub={fmt(totals.quote)} />
          <TotalTile label="Total spend" value={fmtK(totals.spend)} sub={fmt(totals.spend)} />
          <TotalTile label="Margin"
            value={fmtK(totals.margin)}
            sub={`${totals.pct.toFixed(1)}%`}
            tone={filtered.length ? marginTone(totals.pct) : null}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input
              className="w-full border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm"
              placeholder="Search project ID · name · customer · sales person"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="margin-search"
            />
          </div>
          <select className="border border-slate-300 rounded-sm px-3 py-2 text-sm"
            value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            data-testid="margin-stage-filter">
            {stages.map((s) => (
              <option key={s} value={s}>{s === "all" ? "All stages" : s.replaceAll("_", " ")}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="margin-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left">
                <Th>Project</Th>
                <Th>Stage</Th>
                <Th onClick={() => toggleSort("quote_value")} align="right">
                  Quote {arrow("quote_value")}
                </Th>
                <Th align="right">Sheets</Th>
                <Th align="right">POs Raw</Th>
                <Th align="right">Issued</Th>
                <Th align="right">Labour</Th>
                <Th align="right">Transport</Th>
                <Th align="right">Install</Th>
                <Th onClick={() => toggleSort("total_cost")} align="right">
                  Spend {arrow("total_cost")}
                </Th>
                <Th onClick={() => toggleSort("margin_amount")} align="right">
                  Margin ₹ {arrow("margin_amount")}
                </Th>
                <Th onClick={() => toggleSort("margin_percent")} align="right">
                  Margin % {arrow("margin_percent")}
                </Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-slate-400" data-testid="margin-empty">
                  No projects match the current filters.
                </td></tr>
              ) : (
                filtered.map((r) => {
                  const tone = marginTone(r.margin_percent);
                  const trending = r.margin_amount >= 0 ? TrendingUp : TrendingDown;
                  const Icon = trending;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`margin-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <Link to={`/projects/${r.id}`} className="font-mono text-[10px] text-orange-700 hover:underline block truncate max-w-[280px]">
                          {r.project_id || r.id.slice(0, 8)}
                        </Link>
                        <div className="font-bold text-slate-800 truncate max-w-[280px]">{r.name || "—"}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[280px]">
                          {r.customer_name || "—"}{r.sales_person_name ? ` · ${r.sales_person_name}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase font-bold text-slate-600">
                          {(r.stage || "—").replaceAll("_", " ")}
                        </span>
                        {r.sub_status && (
                          <div className="text-[9px] text-slate-400">{r.sub_status.replaceAll("_", " ")}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(r.quote_value)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">
                        {fmt(r.sheet_cost)}
                        {r.counts?.sheets > 0 && (
                          <div className="text-[9px] text-slate-400">{r.sheet_weight_kg} kg · {r.counts.sheets} rows</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">
                        {fmt(r.po_cost)}
                        {r.counts?.purchase_orders > 0 && (
                          <div className="text-[9px] text-slate-400">{r.counts.purchase_orders} POs · not in total</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">
                        {fmt(r.issued_cost)}
                        {r.counts?.issued_movements > 0 && (
                          <div className="text-[9px] text-slate-400">{r.counts.issued_movements} issues</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">
                        {fmt(r.labour_cost)}
                        {r.counts?.hour_entries > 0 && (
                          <div className="text-[9px] text-slate-400">{r.labour_hours} hrs</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt(r.transport_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt(r.installation_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">{fmt(r.total_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: tone.color }}>
                        {fmt(r.margin_amount)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-bold text-[11px]"
                          style={{ color: tone.color, background: tone.bg }}
                          data-testid={`margin-pct-${r.id}`}>
                          <Icon className="w-3 h-3" />
                          {r.margin_percent.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-slate-400">
          Sheets = weight × ₹/kg · POs = sum of PO totals · Labour = (regular + OT×1.5) × station ₹/hr · Transport + Install = expense sums.
          Configure rates in Settings → Costing.
        </div>
      </div>
    </DesktopLayout>
  );
}

function Th({ children, onClick, align = "left" }) {
  const clickable = !!onClick;
  return (
    <th className={`px-3 py-2 font-bold uppercase text-[10px] tracking-wide text-slate-500 ${align === "right" ? "text-right" : "text-left"} ${clickable ? "cursor-pointer hover:text-slate-900 select-none" : ""}`}
      onClick={onClick}
      title={clickable ? "Click to sort" : undefined}
      aria-sort={clickable ? "none" : undefined}>
      {children}
    </th>
  );
}

function TotalTile({ label, value, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="hk-tiny-label">{label}</div>
      <div className="text-2xl font-black tracking-tight mt-1"
        style={tone ? { color: tone.color } : {}}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] font-mono text-slate-500 mt-0.5">
          {sub}
        </div>
      )}
    </div>
  );
}
